import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Lock, Mail, ArrowRight, ClipboardCheck, Shield, CheckCircle } from "lucide-react";
import logo from "@/assets/velocity-logo-light.png";

type View = "login" | "forgot";

const AuditorLogin = () => {
  const [view, setView] = useState<View>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const resetForm = () => { setError(""); setMessage(""); setEmail(""); setPassword(""); };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await signIn(email.trim(), password);
    if (error) {
      setError("Invalid credentials.");
      setLoading(false);
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) { setError("Session not available."); setLoading(false); return; }
    if (session?.user?.user_metadata?.must_change_password) {
      navigate("/change-password");
      setLoading(false);
      return;
    }
    const { data: isAuditor } = await supabase.rpc("has_role", { _user_id: userId, _role: "auditor" as any });
    if (isAuditor) {
      navigate("/auditor");
    } else {
      setError("This portal is restricted to flag-state auditors.");
      await supabase.auth.signOut();
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setMessage("Password reset link sent to your email.");
    setLoading(false);
  };

  const title = view === "login" ? "Sign In" : "Reset Password";
  const subtitle = view === "login"
    ? "Marshall Islands (RMI) flag-state auditor access"
    : "Enter your email to receive a reset link";

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, hsl(218 55% 10%) 0%, hsl(190 40% 18%) 50%, hsl(215 35% 26%) 100%)' }}>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-teal/8 to-transparent rounded-bl-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-teal/5 to-transparent rounded-tr-full" />

        <div className="relative z-10 w-full max-w-md mx-6 py-20">
          <div className="text-center mb-10">
            <a href="/"><img src={logo} alt="Velocity Offshore" className="h-14 mx-auto mb-6 opacity-90" /></a>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-teal/10 border border-teal/20">
              <ClipboardCheck className="w-3.5 h-3.5 text-teal" />
              <span className="text-[10px] font-bold tracking-[2px] uppercase text-teal">Flag State Auditor</span>
            </div>
            <h1 className="font-display text-3xl text-primary-foreground tracking-tight mb-2">
              Auditor <span className="text-teal">Portal</span>
            </h1>
            <p className="text-primary-foreground/40 font-body text-sm">{subtitle}</p>
          </div>

          <div className="glass-card p-8 rounded-2xl">
            {message ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-teal mx-auto mb-4" />
                <p className="text-primary-foreground font-body text-sm">{message}</p>
                <button onClick={() => { resetForm(); setView("login"); }} className="mt-4 text-teal text-sm font-bold hover:underline">Back to Sign In</button>
              </div>
            ) : (
              <>
                <form onSubmit={view === "login" ? handleLogin : handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="auditor@register-iri.com" required />
                    </div>
                  </div>

                  {view === "login" && (
                    <div>
                      <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="••••••••" required />
                      </div>
                    </div>
                  )}

                  {view === "login" && (
                    <button type="button" onClick={() => { resetForm(); setView("forgot"); }} className="text-teal/70 hover:text-teal text-xs font-body transition-colors">
                      Forgot your password?
                    </button>
                  )}

                  {error && <div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-body">{error}</div>}

                  <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-xl hover:shadow-teal/25 transition-all duration-300 font-body disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? "Please wait..." : title}
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                {view === "forgot" && (
                  <button onClick={() => { resetForm(); setView("login"); }} className="mt-4 w-full text-center text-teal/70 hover:text-teal text-xs font-body transition-colors">
                    Back to Sign In
                  </button>
                )}

                {view === "login" && (
                  <p className="text-center text-primary-foreground/25 text-xs font-body mt-5">
                    Credentials are issued by Velocity Offshore DPA. Contact compliance@velocityoffshore.com for access.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 text-primary-foreground/25 text-xs font-body">
              <Shield className="w-3.5 h-3.5" /><span>256-bit Encrypted</span>
            </div>
            <div className="flex items-center gap-2 text-primary-foreground/25 text-xs font-body">
              <ClipboardCheck className="w-3.5 h-3.5" /><span>Read-Only Audit Access</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AuditorLogin;
